import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toastr } from 'react-redux-toastr';

import Loader from '../../../components/Common/Loader/Loader';
import CustomPagination from '../../../components/CustomPagination';
import messages from '../../../locale/messages';
import getAvailableJobsAdmin from './getAvailableJobsAdmin.graphql';
import alertAvailableJobWorkersMutation from './alertAvailableJobWorkers.graphql';

// Admin monitor for broadcast "Available Jobs". Lists every unclaimed Choose-for-me job (all
// regions, live-polled), with WHEN / where / fare / how long it has been waiting, and an
// "Alert workers" action that re-notifies the eligible workers in the zone.
const AvailableJobs = ({ intl: { formatMessage } }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { loading, data, refetch } = useQuery(getAvailableJobsAdmin, {
    variables: { currentPage },
    fetchPolicy: 'network-only',
    ssr: false,
    pollInterval: 15000, // live: jobs appear/disappear as they are broadcast / taken
  });

  const [runAlert, { loading: alerting }] = useMutation(alertAvailableJobWorkersMutation);

  const onAlert = async (bookingId) => {
    try {
      const res = await runAlert({ variables: { bookingId } });
      if (res?.data?.alertAvailableJobWorkers?.status === 200) {
        toastr.success('Success', formatMessage(messages.availableJobsAlertSuccess));
        await refetch();
      } else {
        toastr.error('Error', res?.data?.alertAvailableJobWorkers?.errorMessage || 'Something went wrong');
      }
    } catch (e) {
      toastr.error('Error', 'Something went wrong');
    }
  };

  const fmtWhen = (job) => {
    if (!job?.scheduledAt) return formatMessage(messages.availableJobsImmediate);
    const dt = new Date(parseInt(job.scheduledAt, 10) * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let h = dt.getHours() % 12; if (h === 0) h = 12;
    const mm = `${dt.getMinutes()}`.padStart(2, '0');
    return `${months[dt.getMonth()]} ${dt.getDate()}, ${h}:${mm} ${dt.getHours() < 12 ? 'AM' : 'PM'}`;
  };

  const fmtAge = (secs) => {
    const s = Number(secs) || 0;
    const m = Math.floor(s / 60);
    return m < 1 ? `${s}s` : `${m}m`;
  };

  const results = data?.getAvailableJobsAdmin?.results || [];
  const count = data?.getAvailableJobsAdmin?.count || 0;

  const paginationData = (page) => {
    setCurrentPage(page);
    refetch({ currentPage: page });
  };

  return (
    <Loader type={'page'} show={loading}>
      <h1 className="siteAdminPrimaryHeading"><FormattedMessage {...messages.availableJobs} /></h1>
      {!loading && (
        results.length === 0 ? (
          <p><FormattedMessage {...messages.availableJobsNone} /></p>
        ) : (
          <>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th scope="col"><FormattedMessage {...messages.id} /></th>
                <th scope="col"><FormattedMessage {...messages.availableJobsService} /></th>
                <th scope="col"><FormattedMessage {...messages.availableJobsWhen} /></th>
                <th scope="col"><FormattedMessage {...messages.availableJobsFare} /></th>
                <th scope="col"><FormattedMessage {...messages.availableJobsAge} /></th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {results.map((job) => (
                <tr key={job?.id}>
                  <td>{job?.id}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{job?.service || job?.categoryName}</div>
                    <div style={{ fontSize: 12, color: '#777' }}>{job?.userLocation}</div>
                  </td>
                  <td>{fmtWhen(job)}</td>
                  <td>{job?.estimatedTotalFare != null ? `${job?.currency || ''} ${Number(job.estimatedTotalFare).toFixed(2)}` : '-'}</td>
                  <td>
                    {fmtAge(job?.secondsOnBoard)}
                    {job?.pooled ? ' · on board' : ' · ringing'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={alerting}
                      onClick={() => onAlert(job?.id)}
                    >
                      <FormattedMessage {...messages.availableJobsAlert} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {count > 10 && (
            <CustomPagination
              total={count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={10}
              change={paginationData}
              paginationLabel="jobs"
            />
          )}
          </>
        )
      )}
    </Loader>
  );
};

export default injectIntl(AvailableJobs);
